import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Plus, ArrowLeft, Loader2 } from 'lucide-react';
import { Layout } from '../components/layout';
import { ComparisonPanel } from '../components/comparison';
import { useAppStore } from '../context/store';
import { useCasesByIds } from '../hooks/useCases';
import { useSEO } from '../hooks/useSEO';

const linkButtonStyles = 'inline-flex h-10 items-center gap-2 rounded-lg px-4 text-sm font-medium transition-colors';

export const Compare: React.FC = () => {
  const { comparison, clearComparison, addToComparison } = useAppStore();
  const [searchParams, setSearchParams] = useSearchParams();
  const [initialized, setInitialized] = useState(false);

  // Fetch selected cases from database
  const { cases: selectedCases, loading } = useCasesByIds(comparison.selectedCases);

  useSEO({
    title: selectedCases.length > 0
      ? `Compare ${selectedCases.map(c => c.name).join(' vs ')}`
      : 'Compare PC Cases',
    description: selectedCases.length > 0
      ? `Visual size comparison of ${selectedCases.map(c => `${c.manufacturer} ${c.name}`).join(', ')}. Compare dimensions, GPU clearance, and specifications.`
      : 'Compare PC cases side by side with 2D and 3D visualizations. See exact size differences and specifications.',
  });

  // On mount: load cases from URL ?cases= param if present
  useEffect(() => {
    const cases = searchParams.get('cases');
    if (cases) {
      clearComparison();
      cases.split(',').filter(Boolean).forEach(addToComparison);
    }
    setInitialized(true);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // After initialization: keep URL in sync with comparison state
  useEffect(() => {
    if (!initialized) return;
    const ids = comparison.selectedCases;
    if (ids.length > 0) {
      setSearchParams({ cases: ids.join(',') }, { replace: true });
    } else {
      setSearchParams({}, { replace: true });
    }
  }, [comparison.selectedCases, initialized, setSearchParams]);

  const remaining = 4 - comparison.selectedCases.length;

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div className="flex flex-col gap-2">
            <Link to="/" className="inline-flex w-fit items-center gap-1.5 text-sm text-muted hover:text-fg transition-colors">
              <ArrowLeft size={15} />
              All cases
            </Link>
            <h1 className="text-3xl font-semibold tracking-tightest sm:text-4xl">Compare cases</h1>
            <p className="font-mono text-xs uppercase tracking-label text-muted">
              {comparison.selectedCases.length} of 4 selected
            </p>
          </div>

          {remaining > 0 && comparison.selectedCases.length > 0 && (
            <Link to="/" className={`${linkButtonStyles} border border-line-strong text-fg hover:border-fg hover:bg-surface-2`}>
              <Plus size={16} />
              Add {remaining === 1 ? 'one more case' : `up to ${remaining} more`}
            </Link>
          )}
        </div>

        {/* Loading state */}
        {loading && (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-7 w-7 animate-spin text-muted" aria-label="Loading comparison" />
          </div>
        )}

        {/* Comparison Panel */}
        {!loading && selectedCases.length > 0 && (
          <ComparisonPanel cases={selectedCases} />
        )}

        {/* Empty state */}
        {!loading && selectedCases.length === 0 && (
          <div className="flex flex-col items-center gap-4 rounded-xl border border-dashed border-line-strong bg-blueprint px-6 py-16 text-center">
            <div className="flex items-end gap-2" aria-hidden="true">
              <span className="block h-16 w-7 rounded-[3px] border-[1.5px] border-dashed border-line-strong" />
              <span className="block h-11 w-6 rounded-[3px] border-[1.5px] border-dashed border-line-strong" />
              <span className="block h-20 w-9 rounded-[3px] border-[1.5px] border-dashed border-line-strong" />
            </div>
            <h2 className="text-lg font-semibold tracking-tight">Nothing to compare yet</h2>
            <p className="max-w-sm text-sm text-muted">
              Pick up to four cases from the catalog to see them side by side, drawn to scale.
            </p>
            <Link to="/" className={`${linkButtonStyles} bg-fg text-canvas hover:bg-fg/85`}>
              Browse cases
            </Link>
          </div>
        )}
      </div>
    </Layout>
  );
};
